import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { layout, space, typography, LayoutProps, SpaceProps, TypographyProps } from 'styled-system'
import { FontFamilyType, FontWeightType } from 'utils/fonts'
import { ThemeColor } from 'utils/colors'
import { formatCurrency } from 'utils/formatter'
import { capitalizeFirstLetter, shortenString } from 'utils/string'

import Tooltip from '../Tooltip'

export type TextProps = LayoutProps &
  SpaceProps &
  Omit<TypographyProps, 'fontWeight'> & {
    color?: ThemeColor
    children: string | React.ReactNode
    inline?: boolean
    family?: FontFamilyType
    textTransform?: 'lowercase' | 'uppercase' | 'capitalize' | 'none'
    maxLines?: number
    ellipsis?: boolean
    whiteSpace?: 'nowrap' | 'normal' | 'pre' | 'pre-line' | 'pre-wrap' | 'initial' | 'inherit'
    fontWeight?: FontWeightType
    wordBreak?: 'break-word' | 'break-all' | 'keep-all' | 'normal'
    onClick?: React.MouseEventHandler<HTMLDivElement>
    cursor?: 'pointer' | 'grab' | 'auto'
    format?: 'currency'
    variant?: TextVariant
    textDecoration?: 'underline' | 'line-through' | 'overline' | 'none'
    overflowWrap?: 'normal' | 'break-word' | 'anywhere'
    isCapitalizedFirstLetter?: boolean
    maxLength?: number
  }

export type TextVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle'
  | 'body1'
  | 'body2'
  | 'caption1'
  | 'caption2'
  | 'caption3'
  | 'display'
  | 'link'
  | 'error'
  | 'default'

const decodeHtmlEntities = (() => {
  const textarea = document.createElement('textarea')
  return (text: string) => {
    textarea.innerHTML = text
    return textarea.value
  }
})()

const containsHtmlEntity = (text: string) => /&[#a-zA-Z0-9]+;/.test(text)

const Text = ({ children, maxLength, ...props }: TextProps) => {
  const { t } = useTranslation()
  if (typeof children !== 'string' && typeof children !== 'number') {
    return <BaseText {...props}>{children}</BaseText>
  }

  const rawText = String(children)
  const decodedText = containsHtmlEntity(rawText) ? decodeHtmlEntities(rawText) : rawText
  const translatedText = t(decodedText)

  let formattedText = props.isCapitalizedFirstLetter ? capitalizeFirstLetter(translatedText) : translatedText

  if (props.format === 'currency') {
    formattedText = formatCurrency(formattedText)
  }

  const truncated = maxLength ? shortenString(formattedText, maxLength) : formattedText

  const isTruncated = truncated.length !== formattedText.length

  if (isTruncated) {
    return (
      <Tooltip title={formattedText}>
        <BaseText {...props}>{truncated}</BaseText>
      </Tooltip>
    )
  }

  return <BaseText {...props}>{truncated}</BaseText>
}

export default Text

const BaseText = styled.div<TextProps>`
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  color: ${(p) => p.theme.colors['defaultText']};
  ${(p) => getVariantStyle(p.variant)}
  ${(p) => p.theme.font({ color: p.color, family: p.family, weight: p.fontWeight })};
  ${({ color: _1, family: _2, fontWeight: _3, ...p }) => typography(p)};
  ${layout};
  ${space};
  ${(p) =>
    p.inline &&
    css`
      display: inline-block;
    `};
  ${(p) =>
    p.cursor &&
    css`
      cursor: ${p.cursor};
    `};
  ${(p) =>
    css`
      text-transform: ${p.textTransform ?? 'none'};
    `};
  ${(p) =>
    p.maxLines &&
    css`
      display: -webkit-box; /* not supported on IE */
      -webkit-line-clamp: ${p.maxLines};
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding-bottom: 0;
    `}
  ${(p) =>
    p.ellipsis &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
  ${(p) =>
    p.whiteSpace &&
    css`
      white-space: ${p.whiteSpace};
    `}
  ${(p) =>
    p.wordBreak &&
    css`
      word-break: ${p.wordBreak};
    `}
  ${(p) =>
    p.overflowWrap &&
    css`
      overflow-wrap: ${p.overflowWrap};
    `}
  ${(p) =>
    p.textDecoration &&
    css`
      text-decoration: ${p.textDecoration};
    `}
`

const getVariantStyle = (variant?: TextVariant) => {
  switch (variant) {
    case 'h1':
      return css`
        font-size: 50px;
        font-weight: 500;
        line-height: 1.5;
      `
    case 'h2':
      return css`
        font-size: 36px;
        font-weight: 500;
        line-height: 1.5;
      `
    case 'h3':
      return css`
        font-size: 28px;
        font-weight: 500;
        line-height: 1.5;
      `
    case 'h4':
      return css`
        font-size: 22px;
        font-weight: 700;
        line-height: 1.5;
      `
    case 'h5':
      return css`
        font-size: 16px;
        font-weight: 700;
        line-height: 1.5;
      `
    case 'h6':
      return css`
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
      `
    case 'subtitle':
      return css`
        font-size: 14px;
        font-weight: 400;
        line-height: 16px;
      `
    case 'body1':
      return css`
        font-size: 16px;
        font-weight: 400;
        line-height: 1.2;
      `
    case 'body2':
      return css`
        font-size: 14px;
        font-weight: 400;
        line-height: 1.2;
      `
    case 'caption1':
      return css`
        font-size: 13px;
        font-weight: 500;
        line-height: 1.2;
      `
    case 'caption2':
      return css`
        font-size: 13px;
        font-weight: 400;
        line-height: 1.2;
      `
    case 'caption3':
      return css`
        font-size: 12px;
        font-weight: 500;
        line-height: 1.2;
      `
    case 'display':
      return css`
        font-size: 34px;
        font-weight: 700;
        font-family: Montserrat, Roboto, sans-serif;
        line-height: 1.5;
      `
    case 'link':
      return css`
        font-size: 14px;
        font-weight: 400;
        line-height: 1.2;
        color: ${(p) => p.theme.colors.blue1};
        text-decoration: underline;

        &:hover {
          color: ${(p) => p.theme.colors.blue2};
          cursor: pointer;
        }
      `
    case 'error':
      return css`
        font-weight: 500;
        font-size: 14px;
        line-height: 1.2;
        color: ${(p) => p.theme.colors.red};
      `
    default:
      return null
  }
}
